import classNames from "classnames";
import { type ReactNode, useState } from "react";
import TextareaAutosize, {
  type TextareaAutosizeProps,
} from "react-textarea-autosize";
import { Button } from "../../../../../components/Button/Button.tsx";
import { BaseImage } from "../../../../../components/Content/BaseImage.tsx";
import { ErrorWrapper } from "../../../../../components/Error/ErrorWrapper.tsx";
import { ClickableIcon } from "../../../../../components/Icon/ClickableIcon.tsx";
import { Popover } from "../../../../../components/Popover/Popover.tsx";
import {
  Tooltip,
  type TooltipProps,
} from "../../../../../components/Tooltip/Tooltip.tsx";
import { useAppMutation } from "../../../../../http/useAppMutation.ts";
import { trackEvent } from "../../../../../tracking/tracking.ts";
import type { ModelWithIsCompatibleAndTags } from "../../../../types.ts";
import { ImagePicker } from "../../ImagePicker.tsx";
import { PanelSection } from "../PanelSection.tsx";

type PromptActionProps = {
  displayed: boolean;
  disabled: boolean;
};

export const PromptSection = ({
  prompt,
  setPrompt,
  selectedModels,
  infoContent,
  sectionTitle,
  placeholder,
  errors,
  warnings,
  autoFocus = false,
  disablePromptArea = false,
  onEnterKeyDown,
  promptEnhancerProps,
  promptEditorProps,
  promptFromImageGeneratorProps,
  className,
}: {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels?: ModelWithIsCompatibleAndTags[];
  infoContent?: ReactNode;
  sectionTitle: string;
  placeholder: string;
  errors: string[];
  warnings?: string[];
  autoFocus?: boolean;
  disablePromptArea?: boolean;
  onEnterKeyDown?: () => void;
  promptEnhancerProps?: PromptActionProps;
  promptEditorProps?: PromptActionProps;
  promptFromImageGeneratorProps?: PromptActionProps;
  className?: string;
}) => {
  const [isEnhancementLoading, setIsEnhancementLoading] = useState(false);

  return (
    <PanelSection title={sectionTitle} infoContent={infoContent}>
      <ErrorWrapper errors={errors} warnings={warnings}>
        <PromptLayout className={className} errors={errors} warnings={warnings}>
          <PromptArea
            placeholder={placeholder}
            size="md"
            minRows={2}
            maxRows={16}
            value={prompt}
            onChange={setPrompt}
            onEnterKeyDown={onEnterKeyDown}
            autoFocus={autoFocus}
            disabled={isEnhancementLoading || disablePromptArea}
            className="flex-fill"
          />
          <div className="w-full flex-row justify-between items-center">
            <div className="flex-row-center gap-100">
              {promptEnhancerProps?.displayed ? (
                <PromptEnhancerButton
                  prompt={prompt}
                  selectedModels={selectedModels ?? []}
                  onPromptChange={setPrompt}
                  onGenerating={setIsEnhancementLoading}
                  disabled={promptEnhancerProps.disabled || prompt.length === 0}
                />
              ) : null}
              {promptEditorProps?.displayed ? (
                <PromptEditorButton
                  prompt={prompt}
                  onPromptChange={setPrompt}
                  onGenerating={setIsEnhancementLoading}
                  disabled={promptEditorProps.disabled || prompt.length === 0}
                />
              ) : null}
              {promptFromImageGeneratorProps?.displayed ? (
                <PromptFromImageGeneratorButton
                  onPromptChange={setPrompt}
                  onGenerating={setIsEnhancementLoading}
                  disabled={promptFromImageGeneratorProps.disabled}
                />
              ) : null}
            </div>
            <Tooltip side="right" content="English prompts improve results">
              <div className="p-100">
                <BaseImage
                  className="h-400 aspect-square rounded-200"
                  imageClassName="h-full w-full object-contain object-center"
                  src="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/flag/us.png"
                />
              </div>
            </Tooltip>
          </div>
        </PromptLayout>
      </ErrorWrapper>
    </PanelSection>
  );
};

const PromptTooltipProps: Omit<TooltipProps, "children"> = {
  side: "right",
  delayDuration: 500,
};

const PromptLayout = ({
  children,
  className,
  errors,
  warnings,
}: {
  children: ReactNode;
  className?: string;
  errors?: string[];
  warnings?: string[];
}) => (
  <div
    className={classNames(
      className,
      "w-full flex-col justify-between p-100 rounded-150 gap-100 bg-input-surface-rest",
      "hover:bg-input-surface-hover",
      errors && errors.length > 0
        ? "outline-error"
        : warnings && warnings.length > 0
        ? "outline-warning"
        : "outline-focus",
    )}
  >
    {children}
  </div>
);

const PromptArea = ({
  value,
  onChange,
  placeholder,
  minRows,
  maxRows,
  onEnterKeyDown,
  className,
  ...rest
}: {
  value: string;
  onChange: (text: string) => void;
  placeholder?: string;
  maxRows?: number;
  minRows?: number;
  onEnterKeyDown?: () => void;
  size: "sm" | "md" | "lg";
} & Omit<TextareaAutosizeProps, "size" | "onChange" | "value">) => (
  <TextareaAutosize
    className={classNames(
      "w-full resize-none p-100 max-h-[600px] transparent-scroll-container",
      className,
    )}
    value={value}
    onChange={(event) => {
      onChange(event.target.value);
    }}
    placeholder={placeholder}
    minRows={minRows}
    maxRows={maxRows}
    onKeyDown={(e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onEnterKeyDown?.();
      }
      e.stopPropagation();
    }}
    {...rest}
  />
);

const PromptEnhancerButton = ({
  prompt,
  selectedModels,
  onPromptChange,
  onGenerating,
  disabled,
}: {
  prompt: string;
  selectedModels: ModelWithIsCompatibleAndTags[];
  onPromptChange: (prompt: string) => void;
  onGenerating: (loading: boolean) => void;
  disabled: boolean;
}) => {
  const { mutate: enhanceMutate, isLoading } = useAppMutation({
    path: "prompts/enhance",
    invalidate: [],
    onSuccess: (response: { data: { text: string } }) => {
      onPromptChange(response.data.text);
    },
    onSettled: () => onGenerating(false),
  }).mutation;

  return (
    <Tooltip
      {...PromptTooltipProps}
      content="Enhance the prompt"
      isOpen={disabled ? false : undefined}
    >
      <Button
        title="Enhance"
        onClick={() => {
          onGenerating(true);
          enhanceMutate({
            text: prompt,
            reference_uuids: selectedModels.map((model) => model.uuid),
          });
          trackEvent("prompt_assistant:generate", { type: "add_details" });
        }}
        disabled={disabled}
        variant="quad"
        className="h-600 rounded-100"
        loading={isLoading}
      >
        Enhance
      </Button>
    </Tooltip>
  );
};

const PromptEditorButton = ({
  prompt,
  onPromptChange,
  onGenerating,
  disabled,
}: {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onGenerating: (loading: boolean) => void;
  disabled: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [instructionText, setInstructionText] = useState("");
  const { mutate: editWithAIMutate, isLoading } = useAppMutation({
    path: "prompts/edit-with-ai",
    invalidate: [],
    onSuccess: (response: { data: { text: string } }) => {
      onPromptChange(response.data.text);
    },
    onSettled: () => onGenerating(false),
  }).mutation;

  const generate = () => {
    if (instructionText) {
      onGenerating(true);
      setIsPopoverOpen(false);
      editWithAIMutate({ text: prompt, edit_instruction: instructionText });
      trackEvent("prompt_assistant:generate", {
        type: "edit_with_ai",
        instruction: instructionText,
      });
    }
  };

  return (
    <Popover
      side="right"
      align="start"
      isOpen={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
        setInstructionText("");
      }}
      showCloseButton
      content={
        <div className="w-[256px] flex-col p-300 gap-150 bg-surface-primary-rest">
          <div className="label-md-semibold text-primary">
            Edit prompt with AI
          </div>
          <PromptLayout>
            <PromptArea
              value={instructionText}
              onChange={(text) => {
                setInstructionText(text);
              }}
              size="md"
              placeholder="Replace the dog by a cat, change the hat's color to yellow..."
              minRows={2}
              maxRows={5}
            />
          </PromptLayout>
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled || !instructionText}
            onClick={generate}
          >
            Generate the edited prompt
          </Button>
        </div>
      }
    >
      <ClickableIcon
        name="PencilLine"
        variant="tertiary"
        size="sm"
        className={classNames(
          isPopoverOpen && "!bg-button-surface-tertiary-active",
        )}
        disabled={disabled}
        isLoading={isLoading}
        tooltip={{
          ...PromptTooltipProps,
          isOpen: disabled ? false : undefined,
          content: "Edit the prompt with AI",
        }}
      />
    </Popover>
  );
};

const PromptFromImageGeneratorButton = ({
  onPromptChange,
  onGenerating,
  disabled,
}: {
  onPromptChange: (prompt: string) => void;
  onGenerating: (loading: boolean) => void;
  disabled: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [imageUuid, setImageUuid] = useState<string>();
  const { mutate: describeMutate, isLoading } = useAppMutation({
    path: "contents/describe",
    invalidate: [],
    onSuccess: (response: { data: { description: string } }) => {
      onPromptChange(response.data.description);
    },
    onSettled: () => onGenerating(false),
  }).mutation;

  return (
    <Popover
      side="right"
      align="start"
      isOpen={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
        setImageUuid(undefined);
      }}
      showCloseButton
      content={
        <div className="w-[256px] flex-col p-300 gap-150">
          <div className="label-md-semibold text-primary">
            Get a prompt from an image
          </div>
          <ImagePicker onImageUuidChange={setImageUuid} imageUuid={undefined} />
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled || !imageUuid}
            onClick={() => {
              onGenerating(true);
              setIsPopoverOpen(false);
              describeMutate({ uuid: imageUuid, with_details: true });
              trackEvent("prompt_assistant:generate", {
                type: "from_image",
              });
            }}
          >
            Use this image as prompt
          </Button>
        </div>
      }
    >
      <ClickableIcon
        name="ImagePlus"
        variant="tertiary"
        size="sm"
        disabled={disabled}
        isLoading={isLoading}
        className={classNames(
          isPopoverOpen && "!bg-button-surface-tertiary-active",
        )}
        tooltip={{
          ...PromptTooltipProps,
          isOpen: disabled ? false : undefined,
          content: "Generate prompt from an image",
        }}
      />
    </Popover>
  );
};
